import { inject, injectable } from 'inversify';
import { isFuture, isPast } from 'date-fns';

import { createURL } from '../../../utils/urlFormatting';
import { getDataOrThrow } from '../../../utils/api';
import type { GetPlaylistParams, Playlist, PlaylistItem } from '../../../../types/playlist';
import env from '../../../env';
import ApiContentService from '../../ContentApiService';
import type { Config } from '../../../../types/config';
import { EPG_TIMELINE_DAYS_AFTER, EPG_TIMELINE_DAYS_BEFORE } from '../../../constants';
import { MediaStatus } from '../../../utils/liveEvent';
import type { EpisodeInSeries } from '../../../../types/series';

import type {
  ChannelItem,
  LineUpEventsResponse,
  LineupItemsResponse,
  List,
  ListItem,
  ListItemResource,
  ListItemsResponse,
  ListsResponse,
  RecommendationItemsResponse,
  SearchResponse,
  ShowSeason,
} from './types';
import { fetchWithFingerprint, findImage } from './utils';
import StreamIQData from './StreamIQData';

const contentTypeMap: Record<string, string> = {
  show: 'Series',
  channel: 'LiveChannel',
  episode: 'Episode',
  event: 'LiveProgram',
  'tv-show': 'LiveSeries', // this fetches the latest episode event
};

@injectable()
export default class StreamIQContentApiService extends ApiContentService {
  constructor(@inject(StreamIQData) protected readonly data: StreamIQData) {
    super();
  }

  transformListItem = (listItem: ListItem, playlist?: Playlist): PlaylistItem => {
    const type = listItem.serviceName === 'epg-service' && listItem.type === 'show' ? `tv-show` : listItem.type;
    const contentType = contentTypeMap[type] || 'Movie';
    const startTime = listItem.startTime ? new Date(listItem.startTime) : undefined;
    const endTime = listItem.endTime ? new Date(listItem.endTime) : undefined;
    const mediaStatus =
      startTime && endTime
        ? isPast(startTime) && isFuture(endTime)
          ? MediaStatus.LIVE
          : isPast(endTime)
          ? MediaStatus.VOD
          : MediaStatus.SCHEDULED
        : undefined;

    const trailerId =
      listItem.serviceName !== 'epg-service' && listItem.type !== 'channel' && listItem.type !== 'promotional'
        ? `promotional_${listItem.type}_${listItem.id}`
        : undefined;
    const description = listItem.longDescription || listItem.description || '';
    const product = this.data.getProducts().find((product) => product.id === listItem.id);

    const playlistItem: PlaylistItem = {
      contentType,
      feedid: playlist?.feedid || '',
      mediaid: `${type}_${listItem.id}`,
      genre: listItem.genres?.join(', ') || '',
      duration: typeof listItem.runtime === 'number' ? listItem.runtime * 60 : typeof listItem.duration === 'number' ? listItem.duration / 1000 : 0,
      title: listItem.title || listItem.name, // channel uses `name`
      description,
      cardImage: findImage(listItem.images, !playlist?.cardImageAspectRatio || playlist?.cardImageAspectRatio === '16:9' ? 'landscape' : 'portrait', 'medium'),
      channelLogoImage: findImage(listItem.images, 'landscape', 'medium'),
      backgroundImage: findImage(listItem.images, 'landscape', 'large', 'backdrop'),
      rating:
        listItem.parentalRatings
          ?.filter(({ system }) => system === 'NICAM')
          .map(({ rating }) => rating)
          .join(',') || '',
      directors:
        listItem.credits
          ?.filter((c) => c.role === 'Director' && !/^\d*$/.test(c.name))
          .slice(0, 3)
          .map((c) => c.name)
          .join(', ') || '',
      cast:
        listItem.credits
          ?.filter((c) => c.role === 'Actor' && !/^\d*$/.test(c.name))
          .slice(0, 5)
          .map((c) => c.name)
          .join(', ') || '',
      mediaOffers: product ? [{ id: product.id, premier: true }] : undefined,
      episodeNumber: typeof listItem.episodeNumber === 'number' ? String(listItem.episodeNumber) : undefined,
      seasonNumber: typeof listItem.seasonNumber === 'number' ? String(listItem.seasonNumber) : undefined,
      scheduledStart: startTime,
      scheduledEnd: endTime,
      mediaStatus,
      showId: listItem.showId ? `show_${listItem.showId}` : undefined,
      series_id: listItem.showId && listItem.type === 'episode' ? `show_${listItem.showId}` : undefined,
      trailerId,
      sources: [],
      pubdate: listItem.releaseDate ? new Date(listItem.releaseDate).getTime() / 1000 : 0,
      tracks: [],
      image: '',
      link: '',
      images: [],
      // scheduledStart: this.parseDate(item, 'VCH.ScheduledStart'),
      // scheduledEnd: this.parseDate(item, 'VCH.ScheduledEnd'),
    };

    if (listItem.type === 'channel') {
      const channelItem = listItem as ChannelItem;
      playlistItem.catchupHours =
        channelItem.tstvConfig?.catchUp && channelItem.tstvConfig?.catchUpDurationInDays ? String(channelItem.tstvConfig.catchUpDurationInDays * 24) : '0';
    }

    const lineup = this.data.getLineup();
    if (lineup) {
      playlistItem.lineupId = `lineup_${lineup.id}`;
      playlistItem.liveChannelsId = `lineup_${lineup.id}`;
    }

    if (listItem.type === 'event' && listItem.channelId) {
      playlistItem.liveChannelsId = `channel_${listItem.channelId}`;
    }

    // add the media status to the media item after the transformation because the live media status depends on the scheduledStart and scheduledEnd
    // playlistItem.mediaStatus = getMediaStatusFromEventState(playlistItem);

    return playlistItem;
  };

  protected transformList = (list: List, listItems: ListItemResource[]) => {
    let custom: Record<string, unknown> = {};
    try {
      custom = list.customVariable ? (JSON.parse(list.customVariable) as Record<string, unknown>) : {};
    } catch {
      // no-op
    }

    const customCardAspectRatio = typeof custom?.cardImageAspectRatio === 'string' ? custom.cardImageAspectRatio : undefined;
    const defaultCardImageAspectRatio = custom?.isCardOrientationPriorityHorizontal === false ? '9:13' : '16:9';
    const cardImageAspectRatio = customCardAspectRatio || defaultCardImageAspectRatio;

    const playlist: Playlist = {
      title: list.name,
      description: '',
      playlist: [],
      cardImageAspectRatio,
      feedid: list.id,
    };

    playlist.playlist = listItems.map((listItem) => this.transformListItem(listItem.resource, playlist));

    return playlist;
  };

  protected getListGroupHub = async (id: string) => {
    const listGroupUrl = createURL(`${env.APP_API_BASE_URL}/layout-service/lists`, { listGroupId: id });
    const listGroupResponse = await fetchWithFingerprint(listGroupUrl);
    const lists = (await getDataOrThrow(listGroupResponse)) as ListsResponse;

    return {
      mediaid: id,
      feedid: '',
      contentType: 'Hub',
      title: '',
      description: '',
      playlists: lists.resources.map(({ id }) => id).join(','),
      duration: 0,
      sources: [],
      pubdate: 0,
      image: '',
      link: '',
      images: [],
    } as PlaylistItem;
  };

  protected getEpgLineupItems = async (id: string) => {
    const lineupResponse = await fetchWithFingerprint(`${env.APP_API_BASE_URL}/epg-service/lineups/${id}`);
    const lineup = ((await getDataOrThrow(lineupResponse)) as { id: string; name: string }) || {};
    const lineupItemsUrl = createURL(`${env.APP_API_BASE_URL}/epg-service/lineupItems`, { lineupId: id, resolveReferences: 'true' });
    const listGroupResponse = await fetchWithFingerprint(lineupItemsUrl);
    const lists = (await getDataOrThrow(listGroupResponse)) as LineupItemsResponse;

    return {
      feedid: `lineup_${id}`,
      contentType: 'live',
      title: lineup.name,
      description: '',
      playlist: lists.resources
        .filter((item) => 'resource' in item)
        .map(({ resource: item }) => {
          const catchupHours = item.tstvConfig?.catchUp && item.tstvConfig?.catchUpDurationInDays ? item.tstvConfig.catchUpDurationInDays * 24 : 0;

          return {
            contentType: 'live',
            feedid: `lineup_${id}` || '',
            mediaid: `${item.type}_${item.id}`,
            genre: item.genres?.join(', ') || '',
            duration: 0,
            title: item.name,
            description: item.description || '',
            cardImage: findImage(item.images, 'landscape', 'medium'),
            channelLogoImage: findImage(item.images, 'landscape', 'medium'),
            backgroundImage: findImage(item.images, 'landscape', 'large', 'backdrop'),
            catchupHours: `${catchupHours}`,
            scheduleType: 'streamiq',
            scheduleUrl: item.id,
            mediaOffers: undefined,
            sources: [],
            pubdate: 0,
            tracks: [],
            image: '',
            link: '',
            images: [],
          } as PlaylistItem;
        }),
    } as Playlist;
  };

  /**
   * This fetches episode events for the given showId and returns a single episode that is either
   * - Currently live episode
   * - Most recent VOD episode
   * - Next scheduled episode
   */
  protected getEpgShowEpisodeEvent = async (showId: string) => {
    const pageSize = 999;
    const pathname = createURL('epg-service/events', {
      showId,
      pageSize,
      page: 0,
      startTime: Date.now() - 3600 * 24 * EPG_TIMELINE_DAYS_BEFORE * 1000,
      endTime: Date.now() + 3600 * 24 * EPG_TIMELINE_DAYS_AFTER * 1000,
    });

    const episodesResponse = await fetchWithFingerprint(`${env.APP_API_BASE_URL}/${pathname}`);
    const episodes = (await getDataOrThrow(episodesResponse)) as { resources: ListItem[] };
    const transformedEpisodes = episodes.resources.sort((a, b) => (a.startTime || 0) - (b.startTime || 0)).map((item) => this.transformListItem(item));

    const liveEpisode = transformedEpisodes.find((episode) => episode.mediaStatus === MediaStatus.LIVE);
    const latestVodEpisode = [...transformedEpisodes].reverse().find((episode) => episode.mediaStatus === MediaStatus.VOD);
    const firstScheduledEpisode = transformedEpisodes.find((episode) => episode.mediaStatus === MediaStatus.SCHEDULED);
    const targetEpisode = liveEpisode || latestVodEpisode || firstScheduledEpisode;

    if (!targetEpisode) throw new Error('No episode found for EPG series');

    return targetEpisode;
  };

  getPromotionalForItem = async (id: string) => {
    const referenceType = id.split('_')[0];
    const referenceId = id.split('_').slice(1).join('_');

    const url = createURL(`${env.APP_API_BASE_URL}/vod-service/promotionals`, { referenceId, referenceType });
    const response = await fetchWithFingerprint(url);
    const list = (await getDataOrThrow(response)) as { resources: ListItem[] };
    const mediaItem = list.resources[0];

    if (!mediaItem) throw new Error('MediaItem not found');

    return {
      ...this.transformListItem(mediaItem),
      mediaid: `promotional_${id}`,
    };
  };

  /**
   * Get media by id
   */
  getMediaById = async ({ id, token }: { id: string; token?: string; config: Config }): Promise<PlaylistItem> => {
    const streamIqResource = id.split('_')[0];
    const streamIqId = id.split('_').slice(1).join('_');

    if (streamIqResource === 'listgroup') {
      return this.getListGroupHub(streamIqId);
    }

    if (streamIqResource === 'promotional') {
      return this.getPromotionalForItem(streamIqId);
    }

    const pathname = {
      movie: `/vod-service/movies/${streamIqId}`,
      show: `/vod-service/shows/${streamIqId}`,
      episode: `/vod-service/episodes/${streamIqId}`,
      channel: `/epg-service/channels/${streamIqId}`,
      'tv-show': `/epg-service/shows/${streamIqId}`,
      event: `/epg-service/events/${streamIqId}`,
      movieEvent: `/epg-service/events/${streamIqId}`,
      episodeEvent: `/epg-service/events/${streamIqId}`,
      showEvent: `/epg-service/events/${streamIqId}`,
    }[streamIqResource];

    if (!pathname) throw new Error(`Service not found for id ${id}`);

    const url = createURL(`${env.APP_API_BASE_URL}${pathname}`, { token });
    const response = await fetchWithFingerprint(url);
    const mediaItem = (await getDataOrThrow(response)) as ListItem;

    if (!mediaItem) throw new Error('MediaItem not found');

    return this.transformListItem(mediaItem);
  };

  protected getLiveEventForChannel = async (channelId: string) => {
    const now = Date.now();
    const response = await fetchWithFingerprint(
      createURL(`${env.APP_API_BASE_URL}/epg-service/events`, {
        channelId: channelId,
        startTime: now - 3600 * 1000,
        endTime: now + 3600 * 1000,
      }),
    );
    const data = (await getDataOrThrow(response)) as LineUpEventsResponse;
    const currentEvent = data.resources.find((event) => event.startTime < now && event.endTime > now);

    return currentEvent || data.resources[0];
  };

  protected getRecommendations = async ({ seedId }: { seedId: string; config: Config }) => {
    let streamIqResource = seedId.split('_')[0];
    let streamIqId = seedId.split('_').slice(1).join();

    if (streamIqResource === 'channel') {
      const channelEvent = await this.getLiveEventForChannel(streamIqId);

      if (channelEvent) {
        streamIqResource = channelEvent.type;
        streamIqId = channelEvent.id;
      }
    }

    const isEpgService = ['event', 'episodeEvent', 'movieEvent', 'tv-show'].includes(streamIqResource);
    const recommendationsUrl = createURL(`${env.APP_API_BASE_URL}/recommendation-service/recommendations`, {
      seedServiceName: isEpgService ? 'epg-service' : 'vod-service',
      seedResourceType: streamIqResource === 'tv-show' ? 'show' : streamIqResource,
      seedResourceId: streamIqId,
      recommendationType: 'moreLikeThis',
      resolveReferences: 'true',
    });
    const response = await fetchWithFingerprint(recommendationsUrl);
    const data = (await getDataOrThrow(response)) as RecommendationItemsResponse;

    const items = data.resources.filter((item) => 'resource' in item).map((item) => this.transformListItem(item.resource));

    const playlist = {
      id: 'recommendations',
      title: 'More like this',
      playlist: items,
    } as Playlist;

    return playlist;
  };

  getPlaylistById = async ({ id, params = {}, config }: { id?: string; params?: GetPlaylistParams; config: Config }): Promise<Playlist> => {
    if (!id) {
      throw new Error('is is missing');
    }

    if (id === 'recommendations' && params.related_media_id) {
      return this.getRecommendations({ seedId: params.related_media_id, config });
    }

    const [prefix, playlistId] = id.split('_');

    // playlist menu item labelled "TV Guide" is forced to be an EPG page
    if (playlistId && prefix === 'lineup') {
      return this.getEpgLineupItems(playlistId);
    }

    const listUrl = `${env.APP_API_BASE_URL}/layout-service/lists/${id}`;
    const listItemsUrl = createURL(`${env.APP_API_BASE_URL}/layout-service/listItems`, {
      ...params,
      listId: id,
      resolveReferences: 'true',
    });
    const listResponse = await fetchWithFingerprint(listUrl);
    const response = await fetchWithFingerprint(listItemsUrl);

    const listData = (await getDataOrThrow(listResponse)) as List;
    const listItemsData = (await getDataOrThrow(response)) as ListItemsResponse;

    return this.transformList(
      listData,
      listItemsData.resources.filter((item) => 'resource' in item),
    );
  };

  getSeries = async ({ id }: { id: string }) => {
    const streamIqId = id.split('_').slice(1).join();
    const seasonsResponse = await fetchWithFingerprint(`${env.APP_API_BASE_URL}/vod-service/seasons?showId=${streamIqId}`);
    const seasons = (await getDataOrThrow(seasonsResponse)) as { resources: ShowSeason[] };

    const episodesResponse = await this.getEpisodes({ seriesId: id, pageLimit: 999 });
    const totalDuration = episodesResponse.episodes.reduce((previousValue, currentValue) => {
      return previousValue + currentValue.duration;
    }, 0);

    return {
      series_id: id,
      episode_count: episodesResponse.episodes.length,
      season_count: seasons.resources.length,
      total_duration: totalDuration,
      seasons: seasons.resources
        .sort((a, b) => a.seasonNumber - b.seasonNumber)
        .map((season) => ({
          season_id: season.id,
          season_number: season.seasonNumber,
          season_title: '',
          season_description: season.description,
          episode_count: episodesResponse.episodes.filter((item) => item.seasonId === season.id).length,
          total_duration: episodesResponse.episodes
            .filter((item) => item.seasonId === season.id)
            .reduce((previousValue, currentValue) => {
              return previousValue + currentValue.duration;
            }, 0),
        })),
    };
  };

  getEpisodes = async ({
    seriesId,
    afterId,
    pageOffset = 0,
    pageLimit = 999,
    seasonNumber,
  }: {
    seriesId: string | undefined;
    afterId?: string;
    seasonNumber?: number;
    pageOffset?: number;
    pageLimit?: number;
  }) => {
    const pageSize = 999;
    const [streamIqResource, streamIqId] = seriesId?.split('_') || [];
    const pathname =
      streamIqResource === 'show'
        ? createURL('vod-service/episodes', { showId: streamIqId, pageSize, page: pageOffset })
        : createURL('epg-service/events', {
            showId: streamIqId,
            pageSize,
            page: pageOffset,
            startTime: Date.now() - 3600 * 24 * EPG_TIMELINE_DAYS_BEFORE * 1000,
            endTime: Date.now() + 3600 * 24 * EPG_TIMELINE_DAYS_AFTER * 1000,
          });

    const episodesResponse = await fetchWithFingerprint(`${env.APP_API_BASE_URL}/${pathname}`);
    const episodes = (await getDataOrThrow(episodesResponse)) as { resources: ListItem[] };

    let filteredEpisodes = episodes.resources
      .filter((episode) => {
        if (typeof seasonNumber !== 'undefined' && seasonNumber !== 0) return episode.seasonNumber === seasonNumber;
        return true;
      })
      .sort((a, b) => {
        if (a.seasonNumber !== b.seasonNumber) {
          return Number(a.seasonNumber) - Number(b.seasonNumber);
        }
        return Number(a.episodeNumber) - Number(b.episodeNumber);
      });

    // virtual filter (for resolving the next episode)
    const afterEpisodeId = afterId?.split('_')[1];
    if (afterEpisodeId) {
      filteredEpisodes = filteredEpisodes.slice(filteredEpisodes.findIndex((item) => item.id === afterEpisodeId) + 1);
    }

    // virtual pagination due to sorting is missing in the StreamIQ API, we can't resolve the first episode without
    // requesting them all
    if (pageLimit !== 999) {
      filteredEpisodes = filteredEpisodes.slice(0, pageLimit);
    }

    return {
      episodes: filteredEpisodes.map((item) => this.transformListItem(item)),
      pagination: {
        page: pageOffset + 1,
        page_limit: pageLimit,
        total: filteredEpisodes.length,
      },
    };
  };

  getSeriesByMediaIds = async ({ mediaIds, config }: { mediaIds: string[]; config: Config }) => {
    const bookmarkDictionary = Object.fromEntries(
      this.data.getBookmarks().map((bookmark) => [`${bookmark.resource.type}_${bookmark.resource.id}`, this.transformListItem(bookmark.resource)]),
    );

    const result: Record<string, EpisodeInSeries[]> = {};

    for (const mediaId of mediaIds) {
      const listItem = bookmarkDictionary[mediaId] || (await this.getMediaById({ id: mediaId, config }));

      result[mediaId] =
        typeof listItem.showId === 'string'
          ? [{ series_id: listItem.showId, episode_number: Number(listItem.episodeNumber || '1'), season_number: Number(listItem.seasonNumber || '1') }]
          : [];
    }

    return result;
  };

  search = async ({ searchTerm }: { searchTerm: string; config: Config }) => {
    const url = createURL(`${env.APP_API_BASE_URL}/search-service/resources`, { search: searchTerm, resolveReferences: 'true' });
    const response = await fetchWithFingerprint(url);
    const data = (await getDataOrThrow(response)) as SearchResponse;

    const items = data.resources
      .filter(({ resource }) => !!resource) // Item reference was not resolved
      .filter(({ resource }) => resource.serviceName !== 'epg-service' || !resource.showId)
      .map(({ resource }) => this.transformListItem(resource));

    const playlist = {
      id: 'search',
      title: 'Search results',
      playlist: items,
    } as Playlist;

    return playlist;
  };

  getFavoriteMediaItems = async ({ ids }: { ids: string[]; config: Config; language?: string; token?: string }) => {
    const favorites = this.data.getFavorites().map((item) => this.transformListItem(item.resource));
    const favoritesDictionary = Object.fromEntries(favorites.map((listItem) => [listItem.mediaid, listItem]));

    return ids.map((id) => favoritesDictionary[id]).filter(Boolean);
  };

  getWatchHistoryMediaItems = async ({ ids, config }: { ids: string[]; config: Config }) => {
    const bookmarks = this.data.getBookmarks().map((item) => this.transformListItem(item.resource));
    const bookmarksDictionary = Object.fromEntries(bookmarks.map((listItem) => [listItem.mediaid, listItem]));

    const settledResults = await Promise.allSettled(
      ids.map((id) => {
        if (bookmarksDictionary[id]) return bookmarksDictionary[id];

        return this.getMediaById({ id, config });
      }),
    );

    return settledResults.map((settledResult) => (settledResult.status === 'fulfilled' ? settledResult.value : undefined)).filter((item) => !!item);
  };
}
